import { Header } from "../components/landing/Header";
import { Footer } from "../components/landing/Footer";
import { useSeo } from "../hooks/useSeo";

export default function PrivacyPage() {
  useSeo({
    title: "Privacy Policy | iQ Hub",
    description:
      "Learn how IQ International Pte. Ltd. collects, uses and protects personal information in accordance with applicable data protection laws.",
    ogTitle: "Privacy Policy | iQ Hub",
    ogDescription:
      "Learn how IQ International Pte. Ltd. collects, uses and protects personal information in accordance with applicable data protection laws.",
    ogUrl: "https://www.iq-hub.com/privacy-policy",
    canonical: "https://www.iq-hub.com/privacy-policy",
  });

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-lg max-w-none text-[#475569]">
            <p className="mb-6 text-lg">
              This Privacy Policy explains how IQ International Pte. Ltd (referred to as "the Company") and its trading name 'IQ Hub' collect, use, and protect personal information. References to "IQ Hub," "we," "us," or "our" refer to the relevant data controller within the Company responsible for processing your personal information.
            </p>
            
            <p className="mb-6">
              IQ International Pte. Ltd, as the legal entity and data controller, is responsible for personal data in accordance with this Privacy Policy. By using IQ Hub's services, you consent to the practices described in this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not use our services. We are committed to safeguarding your privacy and ensuring the security of your personal information.
            </p>
            
            <p className="mb-8">
              To update your personal information or marketing preferences, contact us at <a href="mailto:info@iq-hub.com" className="text-[#C8A951] hover:underline">info@iq-hub.com</a>.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">1. Information We Collect</h2>
              <p className="mb-4">We collect various types of personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contact Details:</strong> Your name, email address, postal address, and telephone number.</li>
                <li><strong>Educational, Nationality, and Professional information:</strong> Information relevant to your educational and professional background.</li>
                <li><strong>Usernames and Passwords:</strong> Credentials for accessing our services.</li>
                <li><strong>Payment Information:</strong> Credit or debit card numbers or bank account details for transactions.</li>
                <li><strong>Comments, Feedback, Posts, and other content you submit:</strong> Including survey responses.</li>
                <li><strong>Interests and Communication Preferences:</strong> Your marketing permissions and communication preferences.</li>
                <li><strong>Location Information:</strong> If provided through a mobile app.</li>
                <li><strong>Identity verification information:</strong> Including residency or passport details, to comply with legal obligations and provide visa invitation letters where requested.</li>
                <li><strong>Website and Communication Usage Information:</strong> Details of your use of our website and services, obtained through cookies or other tracking technologies.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">2. Sensitive Personal Information</h2>
              <p>
                If we collect or store sensitive personal information, such as health-related data or information about beliefs or political affiliation, we will request your consent unless certain exceptions apply.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">3. Children's Personal Information</h2>
              <p>
                Our services and products are intended for business professionals and not for children under the age of sixteen. We do not knowingly collect personal information from users in this age group.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">4. How and Why We Use Personal Information</h2>
              <p>
                We use personal information for purposes like product inquiries, sales, administration, event management, publishing services, marketing, customer management, website analytics, and responding to inquiries. These activities help us provide and improve our services, fulfill our contractual obligations, enhance event experiences, manage customer relationships, and ensure legal compliance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">5. Updating Your Information and Preferences</h2>
              <p>
                We are committed to helping you maintain control over your personal information. You can update, correct, or opt out of future communications at any time by sending an email to <a href="mailto:info@iq-hub.com" className="text-[#C8A951] hover:underline">info@iq-hub.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">6. Fulfilling Legal Obligations</h2>
              <p>
                We may process personal information to comply with legal and regulatory requirements, respond to inquiries, investigations, or proceedings, and prevent fraud.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">7. Data Protection Measures</h2>
              <p>
                We handle your personal information with the utmost care and security. Our robust security measures are designed to safeguard your data and control access. We have established comprehensive security and data protection policies to ensure the highest standards of information security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">8. Data Retention</h2>
              <p>
                We retain personal information for as long as necessary for the purposes described in this policy or as required by applicable laws. Afterward, data is securely deleted or destroyed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">9. Data Subject Rights</h2>
              <p>
                You have the right to access, rectify, delete, or restrict the processing of your personal information. To exercise these rights or for privacy-related inquiries, please contact us at <a href="mailto:info@iq-hub.com" className="text-[#C8A951] hover:underline">info@iq-hub.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">10. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and tracking technologies to enhance your online experience and provide you with personalized content. Please refer to our <a href="/cookie-policy" className="text-[#C8A951] hover:underline">Cookie Policy</a> for detailed information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">11. International Data Transfers</h2>
              <p>
                Given the global scope of our operations, personal information may be accessed from various locations worldwide. We take measures to safeguard your data during these transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">12. Third-party Links</h2>
              <p>
                This website may include links to other websites. We are not responsible for the privacy practices of external websites.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">13. Sharing with Third Parties</h2>
              <p>
                We may share personal information with trusted partners and organizations for specific purposes, ensuring compliance with local data protection laws. These may include IT suppliers, web hosting providers, event registration partners, and other service providers necessary to deliver our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">14. Changes to This Privacy Policy</h2>
              <p>
                We may revise this Privacy Policy to stay compliant with evolving laws and best practices. Any updates will be posted on this website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">15. Contact Information</h2>
              <p className="mb-4">
                For privacy-related questions or concerns, contact us at <a href="mailto:info@iq-hub.com" className="text-[#C8A951] hover:underline">info@iq-hub.com</a>.
              </p>
              <p className="text-[#64748B]">
                Privacy Officer at IQ International Pte. Ltd<br />
                8 Marina View, #07-04 Asia Square Tower 1<br />
                Singapore, 018960
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-[#E2E8F0]">
              <p className="text-sm text-[#64748B]">
                Last updated: February 2026
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
